import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';

import { Download, AlertTriangle, Package, Calendar, RotateCcw, TrendingUp, Target } from 'lucide-react';

import { Button } from "@shadcn/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shadcn/components/ui/card";
import { Label } from "@shadcn/components/ui/label";
import { Input } from "@shadcn/components/ui/input";
import { Badge } from "@shadcn/components/ui/badge";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@shadcn/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shadcn/components/ui/tooltip";
import { toast } from "react-toastify";
import { Skeleton } from "@shadcn/components/ui/skeleton";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  ChartTooltip,
  ChartLegend,
  Title
);

const MispickReportPage = () => {
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);

  // Date filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportOrdersLoading, setExportOrdersLoading] = useState(false);
  
  const summaryData = React.useMemo(() => {
    if (!filteredData.length) return {
      totalOrders: 0,
      totalBackordered: 0,
      averageMisPickRate: 0,
      highestMisPickRate: { country: '-', rate: 0 }
    };

    const totalOrders = filteredData.reduce((sum, item) => sum + item.totalOrders, 0);
    const totalBackordered = filteredData.reduce((sum, item) => sum + item.backordered, 0);
    const averageMisPickRate = totalOrders > 0 ? (totalBackordered / totalOrders) * 100 : 0;

    const relevantCountries = filteredData.filter(item => item.totalOrders >= 10);
    let highestMisPickRate = { country: '-', rate: 0 };

    if (relevantCountries.length > 0) {
      highestMisPickRate = relevantCountries.reduce((prev, current) =>
        (current.misPickRate > prev.rate)
          ? { country: current.country, rate: current.misPickRate }
          : prev
        , { country: '-', rate: 0 });
    }

    return {
      totalOrders,
      totalBackordered,
      averageMisPickRate,
      highestMisPickRate
    };
  }, [filteredData]);

  const fetchMispickData = useCallback(async () => {
    try {
      setIsLoading(true);

      // Prepare request body with date filters
      const requestBody = {};
      if (startDate) {
        requestBody.startDate = startDate;
      }
      if (endDate) {
        requestBody.endDate = endDate;
      }

      const response = await superagent
        .post(`${backendUrl}/orders/calculateMisPickRateByShippingCountry`)
        .send(requestBody)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      if (response.body && Array.isArray(response.body)) {
        setReportData(response.body);
        console.log('API response data:', response.body);
      } else {
        console.error('API response format is unexpected:', response.body);
        toast.error("Incorrect data format from API. Please try again later.");
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching mispick data:', error);
      toast.error("Unable to fetch mispick report data. Please try again later.");
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchMispickData();
  }, [fetchMispickData]);

  useEffect(() => {
    let filtered = [...reportData];

    if (selectedCountries.length > 0) {
      filtered = filtered.filter(item => selectedCountries.includes(item.country));
    }

    filtered = filtered.sort((a, b) => b.misPickRate - a.misPickRate);
    setFilteredData(filtered);
  }, [reportData, selectedCountries]);

  const countryOptions = React.useMemo(() => {
    return reportData
      .map(item => item.country)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
  }, [reportData]);

  // Updated chart data with soft color scheme
  const barChartData = React.useMemo(() => {
    const top10Countries = [...filteredData]
      .filter(item => item.misPickRate > 0)
      .sort((a, b) => b.misPickRate - a.misPickRate)
      .slice(0, 10);

    return {
      labels: top10Countries.map(item => item.country),
      datasets: [
        {
          label: 'Mispick Rate (%)',
          data: top10Countries.map(item => item.misPickRate),
          backgroundColor: '#93C5FD', // Blue-300
          borderColor: '#60A5FA', // Blue-400
          borderWidth: 1,
        }
      ]
    };
  }, [filteredData]);

  const pieChartData = React.useMemo(() => {
    const significantCountries = filteredData
      .filter(item => item.misPickRate > 0 && item.totalOrders >= 10)
      .sort((a, b) => b.backordered - a.backordered);

    const topCountries = significantCountries.slice(0, 5);
    const otherCountries = significantCountries.slice(5);
    const otherBackordered = otherCountries.reduce((sum, item) => sum + item.backordered, 0);

    const chartData = [...topCountries];
    if (otherBackordered > 0) {
      chartData.push({
        country: 'Others',
        backordered: otherBackordered
      });
    }

    // Soft color palette
    const softColors = [
      '#93C5FD', // Blue-300
      '#FCA5A5', // Red-300
      '#86EFAC', // Green-300
      '#FBBF24', // Amber-400
      '#C084FC', // Purple-300
      '#FB7185'  // Rose-300
    ];

    return {
      labels: chartData.map(item => item.country),
      datasets: [
        {
          data: chartData.map(item => item.backordered),
          backgroundColor: softColors.slice(0, chartData.length),
          borderColor: '#FFFFFF',
          borderWidth: 2,
        }
      ]
    };
  }, [filteredData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Mispick Rate (%)',
          font: {
            weight: '500'
          }
        },
        grid: {
          color: '#E5E7EB'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#111827',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: '#374151',
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            const value = context.parsed.y || 0;
            return `Mispick Rate: ${value.toFixed(2)}%`;
          }
        }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: '#111827',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: '#374151',
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const dataset = context.dataset;
            const total = dataset.data.reduce((acc, current) => acc + current, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} orders (${percentage}%)`;
          }
        }
      }
    }
  };

  const exportToCsv = () => {
    if (filteredData.length === 0) {
      toast.warn("No data available to export.");
      return;
    }

    const headers = ['Country', 'Total Orders', 'Mispick Orders', 'Mispick Rate (%)'];
    const csvContent = [
      headers.join(','),
      ...filteredData.map(item => [
        `"${item.country}"`,
        item.totalOrders,
        item.backordered,
        item.misPickRate.toFixed(2)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `mispick-report-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    toast.success("CSV report has been downloaded successfully.");
  };

  const exportOrdersList = useCallback(async () => {
    setExportOrdersLoading(true);
    try {
      const query = {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };
      if (selectedCountries.length > 0) {
        query.countries = selectedCountries.join(',');
      }
      const response = await superagent
        .get(`${backendUrl}/orders/mispick-stock/export`)
        .query(query)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      const data = response.body;
      const lines = data?.lines ?? data?.data ?? (Array.isArray(data) ? data : []);

      if (!lines.length) {
        toast.warn("No mispick orders found for the selected filters.");
        setExportOrdersLoading(false);
        return;
      }

      const isArrayOfArrays = lines.length && Array.isArray(lines[0]);
      const dataRowCount = isArrayOfArrays && lines.length > 1 ? lines.length - 1 : lines.length;

      if (!dataRowCount) {
        toast.warn("No mispick orders found for the selected filters.");
        setExportOrdersLoading(false);
        return;
      }

      const escapeCsv = (v) => {
        const s = v == null ? '' : String(v);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const csvContent = isArrayOfArrays
        ? lines.map((row) => row.map(escapeCsv).join(',')).join('\n')
        : [Object.keys(lines[0]).join(','), ...lines.map((row) => Object.values(row).map(escapeCsv).join(','))].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `mispick-orders-list-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${dataRowCount} mispick order(s) successfully.`);
    } catch (error) {
      console.error('Error exporting mispick orders:', error);
      toast.error("Unable to export mispick orders. Please try again later.");
    } finally {
      setExportOrdersLoading(false);
    }
  }, [startDate, endDate, selectedCountries]);

  const handleMultiCountryChange = (value) => {
    if (selectedCountries.includes(value)) {
      setSelectedCountries(selectedCountries.filter(country => country !== value));
    } else {
      setSelectedCountries([...selectedCountries, value]);
    }
  };

  // Date filter handlers
  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const clearDateFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  // Quick date preset handlers
  const setLast7Days = () => {
    const end = new Date();
    const start = subDays(end, 7);
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  const setLast30Days = () => {
    const end = new Date();
    const start = subDays(end, 30);
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  const setLast90Days = () => {
    const end = new Date();
    const start = subDays(end, 90);
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
           
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Mispick Rate Report</h1>
              <p className="text-gray-500 text-sm">Monitor and analyze order picking accuracy across locations</p>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <Card className="mb-8 border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Date Range Filter */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="h-5 w-5 text-gray-700" />
                  <Label className="text-lg font-semibold text-gray-900">Date Range</Label>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="startDate" className="text-sm font-medium text-gray-700 mb-2 block">Start Date</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={startDate}
                      onChange={handleStartDateChange}
                      className="border-gray-300 focus:border-gray-600 focus:ring-gray-600"
                    />
                  </div>
                  <div>
                    <Label htmlFor="endDate" className="text-sm font-medium text-gray-700 mb-2 block">End Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      className="border-gray-300 focus:border-gray-600 focus:ring-gray-600"
                    />
                  </div>
                </div>

                {/* Quick Presets */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={setLast7Days}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={setLast30Days}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                  >
                    Last 30 Days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={setLast90Days}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                  >
                    Last 90 Days
                  </Button>
                  {(startDate || endDate) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearDateFilters}
                      className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>

                {/* Active Filter Display */}
                {(startDate || endDate) && (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Active Filter:</span> {startDate || 'Beginning'} to {endDate || 'Present'}
                    </div>
                  </div>
                )}
              </div>

              {/* Country Filter */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <Label className="text-lg font-semibold text-gray-900">Countries</Label>
                  {selectedCountries.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedCountries([])}
                      className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                    >
                      Clear ({selectedCountries.length})
                    </Button>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-gray-50">
                  {countryOptions.length === 0 ? (
                    <div className="text-gray-500 italic text-sm py-4 text-center w-full">No country data available</div>
                  ) : (
                    countryOptions.map(country => (
                      <Badge
                        key={country}
                        variant={selectedCountries.includes(country) ? "default" : "outline"}
                        className={`cursor-pointer transition-all duration-200 ${
                          selectedCountries.includes(country) 
                            ? 'bg-gray-900 text-white hover:bg-gray-800 border-gray-900' 
                            : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400'
                        }`}
                        onClick={() => handleMultiCountryChange(country)}
                      >
                        {country}
                      </Badge>
                    ))
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Total Orders</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {isLoading ? <Skeleton className="h-8 w-20 bg-gray-200" /> : summaryData.totalOrders.toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-gray-100 rounded-xl">
                  <Package className="h-6 w-6 text-gray-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Mispick Orders</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {isLoading ? <Skeleton className="h-8 w-20 bg-gray-200" /> : summaryData.totalBackordered.toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-gray-100 rounded-xl">
                  <AlertTriangle className="h-6 w-6 text-gray-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Average Rate</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {isLoading ? <Skeleton className="h-8 w-20 bg-gray-200" /> : `${summaryData.averageMisPickRate.toFixed(2)}%`}
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${
                  summaryData.averageMisPickRate > 1 ? 'bg-red-100' :
                  summaryData.averageMisPickRate > 0.5 ? 'bg-yellow-100' : 'bg-green-100'
                }`}>
                  <TrendingUp className={`h-6 w-6 ${
                    summaryData.averageMisPickRate > 1 ? 'text-red-600' :
                    summaryData.averageMisPickRate > 0.5 ? 'text-yellow-600' : 'text-green-600'
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Highest Rate</p>
                  <div className="text-xl font-bold text-gray-900">
                    {isLoading ? (
                      <Skeleton className="h-6 w-full bg-gray-200" />
                    ) : (
                      <>
                        <div className="truncate">{summaryData.highestMisPickRate.country}</div>
                        <div className="text-sm text-gray-600 font-normal">{summaryData.highestMisPickRate.rate.toFixed(2)}%</div>
                      </>
                    )}
                  </div>
                </div>
                <div className="p-3 bg-gray-100 rounded-xl">
                  <Target className="h-6 w-6 text-gray-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Bar Chart */}
          <Card className="lg:col-span-2 border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-semibold text-gray-900">
                Top Countries by Mispick Rate
              </CardTitle>
              <CardDescription className="text-gray-600">
                Countries with the highest picking error rates
              </CardDescription>
            </CardHeader>
                         <CardContent>
               {isLoading ? (
                 <div className="flex items-center justify-center h-[300px]">
                   <div className="flex flex-col items-center gap-3">
                     <div className="animate-spin h-8 w-8 border-4 border-blue-200 border-t-blue-400 rounded-full"></div>
                     <div className="text-sm text-gray-600">Loading chart data...</div>
                   </div>
                 </div>
               ) : barChartData.labels.length > 0 ? (
                <div className="h-[300px]">
                  <Bar data={barChartData} options={chartOptions} />
                </div>
              ) : (
                <div className="flex items-center justify-center h-[300px] border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                  <div className="text-center text-gray-500">
                    <div className="text-4xl mb-3">📊</div>
                    <div className="font-medium text-lg mb-1">No data available</div>
                    <div className="text-sm text-gray-400">Try adjusting your filters</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pie Chart */}
          <Card className="border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-semibold text-gray-900">
                Distribution
              </CardTitle>
              <CardDescription className="text-gray-600">
                Mispick orders by country
              </CardDescription>
            </CardHeader>
                         <CardContent>
               {isLoading ? (
                 <div className="flex items-center justify-center h-[300px]">
                   <div className="flex flex-col items-center gap-3">
                     <div className="animate-spin h-8 w-8 border-4 border-blue-200 border-t-blue-400 rounded-full"></div>
                     <div className="text-sm text-gray-600">Loading chart data...</div>
                   </div>
                 </div>
               ) : pieChartData.labels.length > 0 ? (
                <div className="h-[300px]">
                  <Pie data={pieChartData} options={pieChartOptions} />
                </div>
              ) : (
                <div className="flex items-center justify-center h-[300px] border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                  <div className="text-center text-gray-500">
                    <div className="text-4xl mb-3">🥧</div>
                    <div className="font-medium text-lg mb-1">No data available</div>
                    <div className="text-sm text-gray-400">Try adjusting your filters</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Data Table */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <div>
              <CardTitle className="text-xl font-semibold text-gray-900">
                Detailed Data by Country
              </CardTitle>
              <CardDescription className="text-gray-600 mt-1">
                Complete breakdown of mispick rates and order volumes
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={exportToCsv}
                      disabled={filteredData.length === 0}
                      className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export current data to CSV file</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="outline"
                disabled={filteredData.length === 0}
                onClick={exportOrdersList}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
              >
                {exportOrdersLoading ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-gray-700 rounded-full mr-2 inline-block" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Export mispick orders
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full bg-gray-200" />
                ))}
              </div>
            ) : filteredData.length > 0 ? (
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                      <TableHead className="font-semibold text-gray-900">Country</TableHead>
                      <TableHead className="text-right font-semibold text-gray-900">Total Orders</TableHead>
                      <TableHead className="text-right font-semibold text-gray-900">Mispick Orders</TableHead>
                      <TableHead className="text-right font-semibold text-gray-900">Mispick Rate (%)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((item, index) => (
                      <TableRow key={item.country} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors duration-150`}>
                        <TableCell className="font-medium text-gray-900">{item.country}</TableCell>
                        <TableCell className="text-right text-gray-700">{item.totalOrders.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-gray-700">{item.backordered.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold px-2 py-1 rounded-md text-sm ${
                            item.misPickRate > 1 ? 'text-red-700 bg-red-100' :
                            item.misPickRate > 0.5 ? 'text-yellow-700 bg-yellow-100' : 'text-green-700 bg-green-100'
                          }`}>
                            {item.misPickRate.toFixed(2)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow className="bg-gray-900 text-white hover:bg-gray-900">
                      <TableCell className="font-semibold">Total</TableCell>
                      <TableCell className="text-right font-semibold">{summaryData.totalOrders.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold">{summaryData.totalBackordered.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold">{summaryData.averageMisPickRate.toFixed(2)}%</TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            ) : (
              <div className="flex items-center justify-center p-12 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                <div className="text-center text-gray-500">
                  <div className="text-5xl mb-4">📋</div>
                  <div className="text-xl font-medium mb-2">No data available</div>
                  <div className="text-gray-400">Try adjusting your date range or country filters</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MispickReportPage;
